import { useEffect, useState, useCallback, useRef, MouseEvent, useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { EditorToolbar } from "./EditorToolbar";
import { LoadingSpinner, ProseWrapper, Typography } from "@libs/zapi-ui/custom";
import { RecropModal } from "@/zapi-frontend/widgets/ImageCropper";
import { createEditorExtensions } from "../utils/editor-extensions";
import { createEditorProps } from "../utils/editor-props";
import { createAutoSaveHandler } from "../utils/content-management";
import { useCropManagement } from "../hooks/use-crop-management";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useYdocProvider } from "@/zapi-frontend/shared/hooks/useYdocProvider";
import Collaboration from "@tiptap/extension-collaboration";
import { CollaborationCursor } from "@tiptap/extension-collaboration-cursor";
import { randomColor } from "@/zapi-frontend/utils/colors";
import { DecorationAttrs } from "@/zapi-frontend/widgets/TipTapEditor/model/types";
import { useCommentStore } from "@/zapi-frontend/widgets/CommentsIsland/model/store/commentsStore";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { removeAllCommentMarks, removePersistentCommentMark } from "../utils/removeCommentMark";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { reportErrorToSentry } from "@/zapi-frontend/utils/report-error-to-sentry";
import { getSizeBytes } from "../utils/getSizeBytes";
import { MEMO_BYTE_LIMIT } from "../const/const";
import { nanoid } from "nanoid";
import { STUB_COMMENT_ID } from "../../CommentsIsland/const/const";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";
import { scrollIntoView } from "@/zapi-frontend/shared/lib/scrollIntoView";
import { highlightTextInEditor, highlightTableRowInEditor } from "../lib/editorHighlighting";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { formatRelativeTime } from "@/zapi-frontend/utils/dates";
import { RegenerateButton } from "./_RegenerateButton";

const processedEvents = new Map();

type TTipTapEditorProps = {
  projectId: string;
  content?: string;
  fileName?: string;
  editable?: boolean;
  autoSave?: boolean;
};

export default function TipTapEditor({
  projectId,
  content: _content,
  fileName: fileNameFromProps = "memo.md",
  editable = true,
  autoSave = false
}: TTipTapEditorProps) {
  const fileName = fileNameFromProps;

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [syncInProgress, setSyncInProgress] = useState(false);

  const { selectedProject } = useProjectStore();
  const chatEnabled = selectedProject?.chatEnabled;

  const { activeChapter, updateChapterContent, syncChapterContent, invalidateChapterCroppedImages } = useChapterStore();

  const {
    setSetContent,
    setCommentsUpdated,
    setImgsCropUpdated,
    setGetMarkdown,
    setHighlightText,
    setHighlightTableRow,
    setRemoveCommentMark
  } = useEditorStore();
  const { provider, ydoc, userName, iAmLeader, loading: isConnectingToYdcoProvider } = useYdocProvider(projectId);
  const { createComment, fetchComments } = useCommentStore();
  const { runTimeout } = useScopedTimeout();
  const { openHandler, scrollToBottomCallback } = useProjectSidebarStore();
  const isLaptop = useMediaQuery("(min-width: 984px)");
  const isSynced = !isConnectingToYdcoProvider && !syncInProgress;

  // For temp comment
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const limitExceededRef = useRef(false); // for access inside editor‑props
  limitExceededRef.current = isLimitExceeded;
  const [showCommentButton, setShowCommentButton] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [selection, setSelection] = useState({ from: 0, to: 0 });
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const { firstName, lastName, id: userId } = useMeStore();
  const toolbarInteractionRef = useRef(false);

  const updateLocalContent = useCallback(
    (content: string) => {
      updateChapterContent(activeChapter?.id + "", content, true);
    },
    [updateChapterContent, activeChapter?.id]
  );

  const handleAutoSave = useMemo(
    () => createAutoSaveHandler(autoSave, projectId, iAmLeader),
    [autoSave, projectId, iAmLeader]
  );

  const debouncedUpdate = useCallback(
    (newContent: string) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        updateLocalContent(newContent);

        if (autoSave && projectId) handleAutoSave(newContent);
      }, 250);
    },
    [updateLocalContent, autoSave, projectId, handleAutoSave]
  );

  const collabExtensions =
    !!ydoc && !!provider
      ? [
          Collaboration.configure({
            document: ydoc,
            field: "default"
          }),
          CollaborationCursor.configure({
            provider,
            user: { name: userName, color: randomColor(), connectedSince: new Date().getTime() },
            render: (user) => {
              /* wrapper <span data-user-id="…"> … */
              const cursor = document.createElement("span");
              cursor.classList.add("z-cursor");
              cursor.setAttribute("data-userName", user.name);
              cursor.style.setProperty("--label-bg", user.color);

              /* the blinking caret ▌ */
              const caret = document.createElement("span");
              caret.classList.add("z-caret");
              caret.style.backgroundColor = user.color;

              /* the name bubble */
              const label = document.createElement("span");
              label.classList.add("z-label");
              label.style.backgroundColor = user.color;
              label.textContent = user.name;

              cursor.appendChild(caret);
              cursor.appendChild(label);
              return cursor;
            },
            selectionRender: (user) => {
              const attr: DecorationAttrs = {
                "data-userName": user.name,
                "data-color": user.color,
                class: "z-selection",
                nodeName: "span",
                style: `background-color: ${user.color}40; --label-bg:${user.color};`
              };
              return attr;
            }
          })
        ]
      : [];

  const editor = useEditor(
    {
      immediatelyRender: true,
      extensions: createEditorExtensions(collabExtensions),
      onUpdate: ({ editor, transaction }) => {
        if (isConnectingToYdcoProvider || !transaction.docChanged) return;

        const isOnlyCommentMarkChange = transaction.steps.every((step) => {
          const stepJson = step.toJSON();
          return stepJson.stepType === "addMark" &&
                 (stepJson.mark?.type === "persistentCommentMark" || stepJson.mark?.type === "commentMark");
        });

        if (isOnlyCommentMarkChange) return;

        try {
          const markdown = editor.storage.markdown.getMarkdown();
          const tooBig = getSizeBytes(markdown) > MEMO_BYTE_LIMIT;
          setIsLimitExceeded(tooBig);

          debouncedUpdate(markdown);
        } catch (error) {
          // Silently handle markdown parsing errors
        }
      },
      onFocus: () => {
        setIsEditorFocused(true);
      },
      onBlur: () => {
        setTimeout(() => {
          if (toolbarInteractionRef.current) return;

          const activeElement = document.activeElement;
          const toolbarElements = document.querySelectorAll('[data-toolbar="true"]');
          let isToolbarFocused = false;

          toolbarElements.forEach((toolbar) => {
            if (toolbar.contains(activeElement) || toolbar === activeElement) {
              isToolbarFocused = true;
            }
          });

          const isInToolbar = activeElement?.closest('[data-toolbar="true"]') !== null;

          if (!isToolbarFocused && !isInToolbar) {
            setIsEditorFocused(false);
          }
        }, 150);
      },
      editable: editable && isSynced
    },
    [ydoc, provider, debouncedUpdate, isConnectingToYdcoProvider, activeChapter?.id]
  );

  useEffect(() => {
    if (editor) {
      editor.setEditable(editable && isSynced);
    }
  }, [editor, editable, isSynced]);

  // TODO - do something better than this
  useEffect(() => {
    if (activeChapter?.activeMemo?.mdContent && editor) {
      const currentContent = editor.storage.markdown.getMarkdown();
      if (currentContent !== activeChapter.activeMemo.mdContent) {
        editor.commands.setContent(activeChapter.activeMemo.mdContent);
      }
    }
  }, [activeChapter?.activeMemo?.mdContent, editor]);

  useEffect(() => {
    if (isLaptop) return;

    const handleToolbarTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-toolbar="true"]')) {
        toolbarInteractionRef.current = true;
      }
    };

    const handleToolbarTouchEnd = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-toolbar="true"]')) {
        toolbarInteractionRef.current = true;
        setTimeout(() => {
          toolbarInteractionRef.current = false;
        }, 500);
      }
    };

    document.addEventListener("touchstart", handleToolbarTouchStart, { passive: true });
    document.addEventListener("touchend", handleToolbarTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleToolbarTouchStart);
      document.removeEventListener("touchend", handleToolbarTouchEnd);
    };
  }, [isLaptop]);

  // WARN we need a ref to consistently use latest editor instance, sometimes it can change and render editor usage on closures stale
  const editorRef = useRef(editor);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  const {
    recropModalOpen,
    recropImage,
    handleImageClick,
    handleRecropSuccess,
    applyCropDataToImages,
    setRecropModalOpen
  } = useCropManagement(editor);

  useEffect(() => {
    if (editor) {
      const editorProps = createEditorProps({
        editor,
        projectId,
        fileName,
        onImageClick: handleImageClick,
        limitReached: () => limitExceededRef.current,
        onLimitExceeded: () =>
          reportErrorToSentry(
            `Memo size limit exceeded`,
            `Project:${selectedProject?.name}; Chapter: ${activeChapter?.projectTemplateChapter.name}`,
            "",
            { projectId, chapterId: activeChapter?.id }
          )
      });

      Object.assign(editor.view.props, editorProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, projectId, fileName, handleImageClick, isLimitExceeded]);

  // This function and following effect does two very important things
  // 1. It sets the initial content of the editor when it mounts (keeping track of initialized state).
  // 2. It only initializes memo content after loading is complete, this means that Yjs document and provider are completely synced and no memo duplication will happen.
  const resolveMemoContent = (
    content = "",
    callbacks: { onResolved?: () => void; onError?: () => void; onFinish?: () => void }
  ) => {
    const editor = editorRef.current;
    const { onError, onFinish, onResolved } = callbacks;

    if (!editor || isConnectingToYdcoProvider || !iAmLeader()) {
      onFinish?.();
      return;
    }

    try {
      const currentContent = editor.storage.markdown.getMarkdown();
      if (content && currentContent !== content) {
        editor.commands.setContent(content);
        const size = getSizeBytes(content);
        const tooBig = size > MEMO_BYTE_LIMIT;
        setIsLimitExceeded(tooBig);
        onResolved?.();
      }
      onFinish?.();
    } catch (error) {
      onError?.();
      onFinish?.();
    }
  };
  const contentSyncAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    const chapterId = activeChapter?.id;

    if (projectId && chapterId) {
      setSyncInProgress(true);
      if (contentSyncAbortController.current) contentSyncAbortController.current.abort();
      contentSyncAbortController.current = new AbortController();

      syncChapterContent(chapterId.toString(), contentSyncAbortController.current)
        .then((updatedMemoContent) => {
          if (updatedMemoContent !== null)
            return resolveMemoContent(updatedMemoContent, {
              onFinish: () => {
                setSyncInProgress(false);
              },
              onResolved: () => {
                // placeholder
              }
            });
        })
        .finally(() => {
          setSyncInProgress(false);
        });
    }

    return () => {
      contentSyncAbortController.current?.abort();
    };
  }, [projectId, activeChapter?.id]);

  const commentsUpdated = useCallback(() => {
    provider?.awareness?.setLocalStateField("customEvent", {
      type: "commentChangeEvent",
      payload: { shouldFetch: true, id: nanoid() }
    });
  }, [provider]);

  const imgsCropUpdated = useCallback(() => {
    provider?.awareness?.setLocalStateField("customEvent", {
      type: "imgsCropChangeEvent",
      payload: { shouldFetch: true, id: nanoid() }
    });
  }, [provider]);

  useEffect(() => {
    if (!isConnectingToYdcoProvider && editor) {
      setSetContent((content) => {
        if (document.activeElement === editor.view.dom) {
          return;
        }

        const currentContent = editor.storage.markdown.getMarkdown();
        if (content && currentContent !== content) {
          editor.commands.setContent(content);
          debouncedUpdate(content);
        }
      });
      setCommentsUpdated(commentsUpdated);
      setImgsCropUpdated(imgsCropUpdated);
      setGetMarkdown(() => editor.storage.markdown.getMarkdown());
      setHighlightText((text: string) => {
        highlightTextInEditor(editor, text);
      });
      setHighlightTableRow((originalRow: string, cleanText: string) => {
        highlightTableRowInEditor(editor, originalRow, cleanText);
      });
      setRemoveCommentMark((commentId: string) => {
        removePersistentCommentMark(editor, commentId);
      });
    }
    return () => {
      setSetContent(undefined);
      setCommentsUpdated(undefined);
      setImgsCropUpdated(undefined);
      setGetMarkdown(undefined);
      setHighlightText(undefined);
      setHighlightTableRow(undefined);
      setRemoveCommentMark(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isConnectingToYdcoProvider]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleContainerClick = useCallback(
    (event: MouseEvent) => {
      if (!editor || !editable) return;

      const editorContentElement = containerRef.current?.querySelector(".ProseMirror");
      if (!editorContentElement) return;

      const containerElement = containerRef.current;
      if (!containerElement) return;

      const clickedInsideContainer = containerElement.contains(event.target as Node);
      const clickedInsideEditor = editorContentElement.contains(event.target as Node);

      const target = event.target as HTMLElement;

      // Don't focus editor if clicking on comment-related elements
      if (
        target.closest(".comment-input") ||
        target.closest(".comment-input-container") ||
        target.closest(".temporaryComment")
      ) {
        return;
      }

      if (clickedInsideContainer && !clickedInsideEditor) {
        editor.commands.focus("end");
      }
    },
    [editor, editable]
  );

  useEffect(() => {
    const content = activeChapter?.activeMemo?.mdContent || "";

    if (editor && content && !isConnectingToYdcoProvider) {
      const timeoutId = setTimeout(() => {
        applyCropDataToImages();
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [
    editor,
    activeChapter?.activeMemo?.mdContent,
    activeChapter?.activeMemo?.croppedMemoImages,
    applyCropDataToImages,
    isConnectingToYdcoProvider
  ]);

  const ignoreNextClick = useRef(false);

  useEffect(() => {
    const handleMouseUp = (e: globalThis.MouseEvent) => {
      if (!editor) return;

      const mouseTarget = e.target as HTMLElement;
      if (mouseTarget.closest(".toolbar-comment-button")) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((e as any).isToolbar) {
        setShowCommentButton(false);
        return;
      }

      const { from, to } = editor.state.selection;
      if (from === to) {
        setShowCommentButton(false);
        return;
      }

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const target = e.target as HTMLElement;
      if (!containerRef.current?.contains(target)) {
        setShowCommentButton(false);
        return;
      }

      if (
        target.closest(".comment-input") ||
        target.closest(".comment-input-container") ||
        target.closest(".temporaryComment")
      ) {
        return;
      }

      if (showCommentButton && selection.from === from && selection.to === to) {
        setShowCommentButton(false);
        return;
      }

      const top = e.clientY;
      const left = e.clientX - 16;

      setSelection({ from, to });
      setButtonPosition({ top, left });
      setShowCommentButton(true);
      setShowCommentInput(false);
      ignoreNextClick.current = true;
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [editor, showCommentButton, selection]);

  useEffect(() => {
    if (showCommentInput && commentInputRef.current) {
      setTimeout(() => {
        commentInputRef.current?.focus();
      }, 200);
    }
  }, [showCommentInput]);

  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      // We want to do this on next tick after handle mouse up
      setTimeout(() => {
        if (ignoreNextClick.current) {
          ignoreNextClick.current = false;
          return;
        }
        const target = e.target as HTMLElement;
        const isClickOnCommentElements =
          target.closest(".comment-input") ||
          target.closest(".comment-input-container") ||
          target.closest(".toolbar-comment-button");
        const isClickOnMarkedText = target.closest(".temporaryComment");
        if (!isClickOnCommentElements && !isClickOnMarkedText) {
          setShowCommentButton(false);
          setShowCommentInput(false);
          removeAllCommentMarks(editor);
        }
      }, 10);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [editor, showCommentButton, showCommentInput, selection]);

  // Delegate clicks on persistent comment marks to open sidebar and scroll
  useEffect(() => {
    if (!editor) return;

    const handlePersistentCommentClick = async (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement;
      const markEl = target && target.closest ? target.closest<HTMLElement>(".persistentComment") : null;

      if (markEl) {
        const commentId = markEl.getAttribute("data-persistent-comment-id");
        if (!commentId) return;

        e.preventDefault();
        e.stopPropagation();

        openHandler("comments");

        const chapterId = activeChapter?.id?.toString();
        if (chapterId) await fetchComments(projectId, chapterId);

        const scrollToComment = () => {
          const el = document.getElementById(commentId) as HTMLElement | null;
          if (!el) {
            setTimeout(scrollToComment, 100);
            return;
          }

          scrollIntoView(el, () => {
            el.classList.add("highlighted");
            void el.offsetWidth;

            runTimeout(() => {
              el.classList.remove("highlighted");
            }, 1000);
          });
        };

        runTimeout(scrollToComment, 200);
      }
    };

    document.addEventListener("click", handlePersistentCommentClick);

    return () => {
      document.removeEventListener("click", handlePersistentCommentClick);
    };
  }, [editor, activeChapter?.id, projectId]);

  // Handle custom collaborative events
  useEffect(() => {
    if (!provider?.awareness) return;
    const localClientId = provider.awareness.clientID;
    // Listen for events from other users
    provider.awareness.on("update", () => {
      const states = provider.awareness?.getStates();
      states?.forEach((state, clientId) => {
        if (clientId === localClientId) return;
        const ev = state.customEvent;
        if (!ev?.payload?.id) return;
        if (processedEvents.has(ev.payload.id)) return;
        processedEvents.set(ev.payload.id, true);
        if (ev.type === "commentChangeEvent" && ev.payload?.shouldFetch) {
          fetchComments(projectId, activeChapter?.id + "");
        } else if (ev.type === "imgsCropChangeEvent" && ev.payload?.shouldFetch) {
          invalidateChapterCroppedImages(activeChapter?.id + "");
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const handleCommentSubmit = useCallback(
    async (commentContent: string) => {
      if (!editor || !activeChapter?.id) return;

      setIsSubmitting(true);
      try {
        // Get the position of the existing commentMark
        let markFrom = 0;
        let markTo = 0;
        editor.state.doc.descendants((node, pos) => {
          if (!node.isText) return;
          const hasMark = node.marks.some((mark) => mark.type.name === "commentMark");
          if (hasMark) {
            markFrom = pos;
            markTo = pos + node.nodeSize;
          }
        });

        // Create the comment
        if (!projectId || !activeChapter?.id || !userId) return;
        const { id: commentId } = await createComment({
          projectId,
          chapterId: activeChapter.id.toString(),
          content: commentContent,
          stubComment: {
            id: STUB_COMMENT_ID,
            content: commentContent,
            createdAt: new Date().toISOString(),
            author: { id: userId, firstName, lastName },
            parent: null,
            replies: []
          }
        });

        // Replace the mark with temporary id to have a comment id
        editor.commands.setTextSelection({ from: markFrom, to: markTo });
        editor.commands.setMark("persistentCommentMark", {
          commentId: commentId
        });
        // Remove temporary comment mark
        removeAllCommentMarks(editor);
        editor.commands.setTextSelection({ from: 0, to: 0 });

        setShowCommentButton(false);
        setShowCommentInput(false);
        // switchToCommentsAndScroll();
        openHandler("comments");
        runTimeout(() => {
          scrollToBottomCallback?.();
        }, 100);
      } finally {
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, activeChapter, projectId, createComment, firstName, lastName]
  );

  const hasContent = !!activeChapter?.activeMemo?.mdContent;

  if (!isSynced && !hasContent) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-gray-500 gap-2">
        <LoadingSpinner />
        Connecting...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-md flex flex-col h-full cursor-text relative">
      {!isSynced && hasContent && (
        <div className="absolute top-2 right-2 text-xs text-gray-400 flex items-center gap-1 z-10 bg-white/80 px-2 py-1 rounded">
          <div className="h-3 w-3 border border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          Syncing...
        </div>
      )}
      {editable && isSynced && isEditorFocused && !isLaptop && (
        <EditorToolbar
          editor={editor}
          fileName={fileName}
          showCommentButton={showCommentButton && !showCommentInput}
          onCommentClick={() => {
            if (editor) {
              editor.commands.setMark("commentMark", {});
              const { from } = editor.state.selection;
              editor.commands.setTextSelection(from);
            }

            setShowCommentInput(true);
            setShowCommentButton(false);
          }}
        />
      )}
      <div className="h-[calc(100%-50px)] overflow-y-scroll relative">
        {activeChapter?.projectTemplateChapter.isExecutiveSummary && (
          <div className="flex flex-col gap-3 py-3 border-b border-innoveraZinc-150 mb-4 mt-[-12px]">
            <div className="flex items-center justify-between">
              {activeChapter?.lastGeneratedAt && (
                <Typography variant="body3" color="zinc500">
                  Last time generated: {formatRelativeTime(activeChapter.lastGeneratedAt)}
                </Typography>
              )}
              {chatEnabled && <RegenerateButton />}
            </div>
          </div>
        )}

        <ProseWrapper
          className="h-fit overflow-visible"
          editable={editable}
          ref={containerRef}
          onClick={handleContainerClick}
        >
          <EditorContent
            className="focus-visible:outline-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror-focused]:outline-none text-zinc-700 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
            editor={editor}
            max={1}
            maxLength={10}
            type="text"
          />
          {showCommentInput && (
            <div
              className="comment-input-container fixed bg-white border border-gray-200 rounded-md shadow-lg p-2"
              style={{
                top: buttonPosition.top - 2,
                left: buttonPosition.left - 2,
                zIndex: 1000,
                minWidth: "200px",
                maxWidth: "300px",
                padding: isSubmitting ? "12px 12px 12px 12px" : undefined
              }}
            >
              <div className="flex items-center gap-2">
                <input
                  ref={commentInputRef}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Type your comment..."
                  disabled={isSubmitting}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isSubmitting) {
                      if (!editor) return;

                      const input = e.target as HTMLInputElement;
                      const inputValue = input.value;

                      if (!activeChapter?.id) {
                        removeAllCommentMarks(editor);
                        return;
                      }

                      handleCommentSubmit(inputValue).then(() => {
                        commentsUpdated();
                      });
                    } else if (e.key === "Escape" && !isSubmitting) {
                      setShowCommentInput(false);
                      removeAllCommentMarks(editor);
                    }
                  }}
                />
                {isSubmitting && (
                  <div
                    className="flex-shrink-0"
                    style={{
                      width: 18,
                      height: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <LoadingSpinner />
                  </div>
                )}
              </div>
            </div>
          )}
        </ProseWrapper>
      </div>
      {((editable && !isEditorFocused) || isLaptop) && (
        <EditorToolbar
          editor={editor}
          fileName={fileName}
          showCommentButton={showCommentButton && !showCommentInput}
          onCommentClick={() => {
            if (editor) {
              editor.commands.setMark("commentMark", {});
              const { from } = editor.state.selection;
              editor.commands.setTextSelection(from);
            }

            setShowCommentInput(true);
            setShowCommentButton(false);
          }}
        />
      )}
      {recropModalOpen && recropImage && (
        <RecropModal
          isOpen={recropModalOpen}
          onClose={() => setRecropModalOpen(false)}
          onSuccess={handleRecropSuccess}
          imageUrl={recropImage.imageUrl}
          currentCropCoordinates={recropImage.cropCoordinates}
        />
      )}
    </div>
  );
}
